/*
 * The Configuration section (admin-ui.md, ADR-0005, API-10): a typed, registry-governed editor
 * plus an Audit tab. Only CONFIG_REGISTRY keys render — there is no free-form key creation.
 * Secrets never appear (env-only). `config:view` sees the section; `config:manage` edits.
 */
import { useEffect, useState } from 'react';
import {
  Banner,
  Button,
  Card,
  Input,
  Table,
  TCell,
  THead,
  TRow,
  Tabs,
  Textarea,
  Toggle,
} from '@cou/admin-ui';
import { AdminShell } from '../shell/AdminShell';
import { useAdminAuth } from '../session/admin-auth-context';
import {
  defaultConfigClient,
  type ConfigAuditEntry,
  type ConfigClient,
} from '../config/config-client';
import {
  CONFIG_REGISTRY,
  type ConfigRegistryEntry,
  registryEntry,
} from '../config/config-registry';

export interface ConfigurationScreenProps {
  /** Injected for tests; defaults to the real API-10 client. */
  readonly client?: ConfigClient;
}

type TabKey = 'editor' | 'audit';

export function ConfigurationScreen({ client = defaultConfigClient }: ConfigurationScreenProps) {
  const { claims, getToken } = useAdminAuth();
  const canManage = claims?.permissions.includes('config:manage') ?? false;
  const [tab, setTab] = useState<TabKey>('editor');
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [audit, setAudit] = useState<readonly ConfigAuditEntry[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [auditError, setAuditError] = useState<string | null>(null);

  useEffect(() => {
    const token = getToken();
    if (token === null) {
      setLoading(false);
      setLoadError('Not signed in.');
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const result = await client.getConfig(token);
      if (cancelled) return;
      if (result.ok) {
        const next: Record<string, unknown> = {};
        const nextDrafts: Record<string, string> = {};
        for (const entry of CONFIG_REGISTRY) {
          if (Object.prototype.hasOwnProperty.call(result.config, entry.key)) {
            next[entry.key] = result.config[entry.key];
          }
          nextDrafts[entry.key] = serializeDraft(entry, result.config[entry.key]);
        }
        setValues(next);
        setDrafts(nextDrafts);
        setLoadError(null);
      } else {
        setLoadError(
          result.code === 'not_implemented'
            ? 'Configuration API is not implemented yet (API-10 stub).'
            : `Could not load configuration (${result.code ?? result.status}).`,
        );
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, getToken]);

  useEffect(() => {
    if (tab !== 'audit') return;
    const token = getToken();
    if (token === null) return;
    let cancelled = false;
    void (async () => {
      const result = await client.getConfigAudit(token);
      if (cancelled) return;
      if (result.ok) {
        setAudit(result.entries);
        setAuditError(null);
      } else {
        setAudit([]);
        setAuditError(
          result.code === 'not_implemented'
            ? 'Configuration audit is not implemented yet.'
            : `Could not load audit (${result.code ?? result.status}).`,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, client, getToken]);

  async function saveKey(key: string): Promise<void> {
    const entry = registryEntry(key);
    if (entry === null || !canManage) return;
    const token = getToken();
    if (token === null) return;
    setSaveOk(null);
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    let parsed: unknown;
    try {
      parsed = parseDraft(entry, drafts[key] ?? '');
    } catch (error) {
      setFieldErrors((prev) => ({
        ...prev,
        [key]: error instanceof Error ? error.message : 'Invalid value.',
      }));
      return;
    }
    const result = await client.putConfig(token, key, parsed);
    if (result.ok) {
      setValues((prev) => ({ ...prev, [key]: result.value }));
      setDrafts((prev) => ({ ...prev, [key]: serializeDraft(entry, result.value) }));
      setSaveOk(`Saved ${entry.label}.`);
    } else {
      setFieldErrors((prev) => ({
        ...prev,
        [key]: result.message ?? `Could not save ${entry.label} (${result.code ?? result.status}).`,
      }));
    }
  }

  return (
    <AdminShell
      title="Configuration"
      subtitle="Registry-governed operational settings. Secrets stay in env and never appear here."
    >
      <Tabs
        tabs={[
          { key: 'editor', label: 'Editor' },
          { key: 'audit', label: 'Audit' },
        ]}
        selected={tab}
        onSelect={(key) => setTab(key as TabKey)}
      />

      {tab === 'editor' && (
        <div className="mt-6 flex flex-col gap-4">
          {!canManage && (
            <Banner variant="info" title="Read only">
              Your role has config:view. Editing requires config:manage.
            </Banner>
          )}
          {loadError && (
            <Banner variant="danger" role="alert">
              {loadError}
            </Banner>
          )}
          {saveOk && (
            <Banner variant="success" role="status">
              {saveOk}
            </Banner>
          )}
          {loading && <p className="font-ui text-[13px] text-ink-3">Loading configuration…</p>}
          {!loading &&
            !loadError &&
            CONFIG_REGISTRY.map((entry) => (
              <ConfigKeyCard
                key={entry.key}
                entry={entry}
                draft={drafts[entry.key] ?? ''}
                error={fieldErrors[entry.key]}
                readOnly={!canManage}
                onDraftChange={(next) => {
                  setDrafts((prev) => ({ ...prev, [entry.key]: next }));
                  setFieldErrors((prev) => {
                    if (!(entry.key in prev)) return prev;
                    const cleared = { ...prev };
                    delete cleared[entry.key];
                    return cleared;
                  });
                }}
                onToggle={(checked) => {
                  setValues((prev) => ({ ...prev, [entry.key]: checked }));
                  setDrafts((prev) => ({ ...prev, [entry.key]: checked ? 'true' : 'false' }));
                  setFieldErrors((prev) => {
                    if (!(entry.key in prev)) return prev;
                    const cleared = { ...prev };
                    delete cleared[entry.key];
                    return cleared;
                  });
                }}
                toggleChecked={Boolean(values[entry.key])}
                onSave={() => void saveKey(entry.key)}
              />
            ))}
        </div>
      )}

      {tab === 'audit' && (
        <div className="mt-6 flex flex-col gap-4">
          {auditError && (
            <Banner variant="warn" role="status">
              {auditError}
            </Banner>
          )}
          {!auditError && audit.length === 0 && (
            <Banner variant="info">No configuration changes recorded yet.</Banner>
          )}
          {audit.length > 0 && (
            // minmax(0, …) so long JSON can shrink/wrap inside the grid (minmax(Npx) overflows).
            <Table columns="minmax(0,1.1fr) minmax(0,1.6fr) minmax(0,1.6fr) minmax(0,0.9fr) minmax(0,1fr)">
              <THead>
                <TCell>Key</TCell>
                <TCell>Old</TCell>
                <TCell>New</TCell>
                <TCell>By</TCell>
                <TCell>When</TCell>
              </THead>
              {audit.map((row) => (
                <TRow key={row.id} className="items-start">
                  <TCell variant="primary">{row.key}</TCell>
                  <TCell variant="mono" className="whitespace-pre-wrap break-all">
                    {formatAuditValue(row.oldValue)}
                  </TCell>
                  <TCell variant="mono" className="whitespace-pre-wrap break-all">
                    {formatAuditValue(row.newValue)}
                  </TCell>
                  <TCell variant="mono" className="break-all">
                    {row.changedBy}
                  </TCell>
                  <TCell variant="mono" className="break-all">
                    {row.changedAt}
                  </TCell>
                </TRow>
              ))}
            </Table>
          )}
        </div>
      )}
    </AdminShell>
  );
}

interface ConfigKeyCardProps {
  readonly entry: ConfigRegistryEntry;
  readonly draft: string;
  readonly error?: string;
  readonly readOnly: boolean;
  readonly onDraftChange: (next: string) => void;
  readonly onToggle: (checked: boolean) => void;
  readonly toggleChecked: boolean;
  readonly onSave: () => void;
}

function ConfigKeyCard({
  entry,
  draft,
  error,
  readOnly,
  onDraftChange,
  onToggle,
  toggleChecked,
  onSave,
}: ConfigKeyCardProps) {
  return (
    <Card
      eyebrow={`${entry.classification} · ${entry.key}`}
      title={entry.label}
      action={
        !readOnly ? (
          <Button size="sm" onClick={onSave} aria-label={`Save ${entry.label}`}>
            Save
          </Button>
        ) : undefined
      }
    >
      <p className="font-ui text-[13px] leading-[1.5] text-ink-3">{entry.description}</p>
      {entry.control === 'toggle' ? (
        <Toggle
          label={entry.label}
          checked={toggleChecked}
          disabled={readOnly}
          onCheckedChange={onToggle}
        />
      ) : entry.control === 'number' ? (
        <Input
          label="Value"
          type="number"
          mono
          value={draft}
          disabled={readOnly}
          error={error}
          onChange={(event) => onDraftChange(event.target.value)}
        />
      ) : entry.control === 'nullable-text' || entry.control === 'text' ? (
        <Input
          label="Value"
          value={draft}
          disabled={readOnly}
          hint={entry.control === 'nullable-text' ? 'Leave blank for null.' : undefined}
          error={error}
          onChange={(event) => onDraftChange(event.target.value)}
        />
      ) : (
        <Textarea
          label="Value"
          mono
          rows={entry.control === 'string-list' ? 4 : 6}
          value={draft}
          disabled={readOnly}
          hint={entry.control === 'string-list' ? 'One entry per line.' : 'JSON object or array.'}
          error={error}
          onChange={(event) => onDraftChange(event.target.value)}
        />
      )}
    </Card>
  );
}

function serializeDraft(entry: ConfigRegistryEntry, value: unknown): string {
  if (value === undefined) {
    if (entry.control === 'toggle') return 'false';
    if (entry.control === 'number') return '';
    if (entry.control === 'nullable-text') return '';
    if (entry.control === 'string-list') return '';
    return entry.control === 'json' ? '{}' : '';
  }
  if (entry.control === 'toggle') return value ? 'true' : 'false';
  if (entry.control === 'number') {
    return typeof value === 'number' ? String(value) : '';
  }
  if (entry.control === 'nullable-text' || entry.control === 'text') {
    if (value === null) return '';
    return typeof value === 'string' ? value : JSON.stringify(value);
  }
  if (entry.control === 'string-list') {
    return Array.isArray(value)
      ? value.map((item) => (typeof item === 'string' ? item : JSON.stringify(item))).join('\n')
      : '';
  }
  return JSON.stringify(value, null, 2);
}

function parseDraft(entry: ConfigRegistryEntry, draft: string): unknown {
  switch (entry.control) {
    case 'toggle':
      return draft === 'true';
    case 'number': {
      if (draft.trim() === '') {
        throw new Error(`${entry.label} must be a number.`);
      }
      const n = Number(draft);
      if (!Number.isFinite(n)) throw new Error(`${entry.label} must be a number.`);
      return n;
    }
    case 'text':
      return draft;
    case 'nullable-text':
      return draft.trim() === '' ? null : draft;
    case 'string-list':
      return draft
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    case 'json':
      if (draft.trim() === '') {
        throw new Error(`${entry.label} must be valid JSON.`);
      }
      try {
        return JSON.parse(draft) as unknown;
      } catch {
        throw new Error(`${entry.label} must be valid JSON.`);
      }
    default:
      return draft;
  }
}

/** Compact scalars; pretty-print objects/arrays so Audit Old/New cells stay readable. */
function formatAuditValue(value: unknown): string {
  if (value === null || value === undefined) return String(value);
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '[unserializable]';
  }
}
